/**
 * The error vocabulary the UI branches on. Every failed request becomes one of
 * these — components never look at raw status codes or response bodies.
 *
 * Authorization is the API's job: the UI hides actions the caller's grants
 * don't cover, but a 403 can always arrive anyway and must render as a clear
 * message, never a crash.
 */
export class ApiError extends Error {
  /** HTTP status, or 0 when the request never reached the server. */
  readonly status: number;
  /** The API's own `reason` field, when the body carried one. */
  readonly reason: string | undefined;

  constructor(status: number, message: string, reason?: string) {
    super(reason ? `${message}: ${reason}` : message);
    this.name = new.target.name;
    this.status = status;
    this.reason = reason;
  }
}

/** 401 — no token, or an expired/rejected one. Re-authentication territory. */
export class UnauthorizedError extends ApiError {}

/** 403 — authenticated, but the caller's grants don't cover this action. */
export class ForbiddenError extends ApiError {}

/** 404 — the resource is gone or was never visible to this caller. */
export class NotFoundError extends ApiError {}

/** 409 — the write conflicts with current state (duplicate, stale, in use). */
export class ConflictError extends ApiError {}

/** The request never completed: DNS, offline, CORS, aborted server. */
export class NetworkError extends ApiError {
  constructor(message: string) {
    super(0, message);
  }
}

export function errorForStatus(status: number, message: string, reason?: string): ApiError {
  switch (status) {
    case 401:
      return new UnauthorizedError(status, message, reason);
    case 403:
      return new ForbiddenError(status, message, reason);
    case 404:
      return new NotFoundError(status, message, reason);
    case 409:
      return new ConflictError(status, message, reason);
    default:
      return new ApiError(status, message, reason);
  }
}
